import { logger } from 'src/helpers/logger';
import type { RateCardSideId } from './constants';

// Long enough for the browser to have opened the blob: `link.click()` starts the download asynchronously,
// and releasing the blob underneath can cancel the transfer.
const BLOB_RELEASE_DELAY_MS = 60_000;

const OPENABLE_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:', 'blob:']);

const parseReportUrl = (url: string): URL | null => {
  try {
    return new URL(url, window.location.origin);
  } catch {
    return null;
  }
};

// Hands a file to the browser — a server URL, or a blob URL built here and released once the transfer has started.
// Only a web or blob url is handed over: anything else is a bad server value, not something to open in a new tab.
// Answers whether it was handed over. A refusal logs the protocol only: a pre-signed query must not reach the logs.
export const triggerDownload = (url: string, fileName?: string | null): boolean => {
  const parsed = parseReportUrl(url);

  if (!parsed || !OPENABLE_PROTOCOLS.has(parsed.protocol)) {
    logger.error(`Refusing to open a non-web url: ${parsed ? parsed.protocol : 'an unparseable url'}`);

    return false;
  }

  const link = document.createElement('a');

  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';

  if (fileName) link.download = fileName;

  link.click();

  if (parsed.protocol === 'blob:') {
    window.setTimeout(() => URL.revokeObjectURL(url), BLOB_RELEASE_DELAY_MS);
  }

  return true;
};

export const DOWNLOAD_OUTCOMES = { SAVED: 'saved', OPENED: 'opened', EXPIRED: 'expired', OFFLINE: 'offline' } as const;

export type DownloadOutcome = (typeof DOWNLOAD_OUTCOMES)[keyof typeof DOWNLOAD_OUTCOMES];

const urlHost = (url: string): string => parseReportUrl(url)?.host ?? 'an unparseable url';

/**
 * Fetched into a blob, because `download` is ignored cross-origin and the file would render in a tab. A refused
 * fetch hands the url to triggerDownload unless offline, and a url it will not open is reported as expired; a non-2xx
 * answer is an expired signature, so nothing opens.
 */
export const downloadFile = async (url: string, fileName: string): Promise<DownloadOutcome> => {
  let response: Response;

  try {
    response = await fetch(url);
  } catch (error) {
    if (navigator.onLine === false) {
      logger.warn(`Offline fetching ${fileName}`);

      return DOWNLOAD_OUTCOMES.OFFLINE;
    }

    const reason = error instanceof Error ? error.message : String(error);

    logger.warn(`Fetch of ${urlHost(url)} refused, opening the link instead: ${reason}`);

    return triggerDownload(url, fileName) ? DOWNLOAD_OUTCOMES.OPENED : DOWNLOAD_OUTCOMES.EXPIRED;
  }

  if (!response.ok) {
    logger.warn(`${response.status} fetching ${fileName}`);

    return DOWNLOAD_OUTCOMES.EXPIRED;
  }

  try {
    triggerDownload(URL.createObjectURL(await response.blob()), fileName);

    return DOWNLOAD_OUTCOMES.SAVED;
  } catch (error) {
    logger.warn(error);

    return triggerDownload(url, fileName) ? DOWNLOAD_OUTCOMES.OPENED : DOWNLOAD_OUTCOMES.EXPIRED;
  }
};

/**
 * A name the user can tell apart from the other side's report: the url's own is a bare id, so only its
 * extension is kept — the report comes back in the format the file was uploaded in.
 */
export const errorReportFileName = (url: string, side: RateCardSideId): string => {
  const parsed = parseReportUrl(url);

  if (!parsed) logger.warn('Unparseable error report url');

  const extension = parsed?.pathname.match(/\.([a-z0-9]+)$/i)?.[1] ?? 'csv';

  return `${side}_rate_card_error_report.${extension}`;
};
